/* eslint react-hooks/exhaustive-deps: 0 */

import React from 'react';
import Cookies from 'js-cookie';
import Environment from '../../../../../../utils/environment';
import { defaultReservationFare, defaultReservationLabels, ReservationFare, ReservationLabels } from '../../../../../../services/taxi/Reservation/ReservationResponse';

/**
 * 空港送迎タクシーの本予約画面の情報に関するカスタムフック。
 */
export function useForm() {
  /** リクエスト回数。 */
  const [requestCount, setRequestCount] = React.useState(1);
  /** 本予約に失敗したかを表すフラグ。 */
  const [isFailed, setIsFailed] = React.useState(false);
  /** 予約処理の再実行を促すかを表すフラグ。 */
  const [isRequiredRetry, setIsRequiredRetry] = React.useState(false);
  /** 利用できないメールアドレスが設定されているかを表すフラグ。 */
  const [isUnavalableEmail, setIsUnavalableEmail] = React.useState(false);
  /** 入力内容に不備があることを表すフラグ。 */
  const [isInvalidInput, setIsInvalidInput] = React.useState(false);

  // フォームで設定する各項目の値を管理するState。
  /** 代表者氏名の姓。 */
  const [lastName, setLastName] = React.useState('');
  /** 代表者氏名の名。 */
  const [firstName, setFirstName] = React.useState('');
  /** 代表者氏名（全角カナ）の姓。 */
  const [lastNameKana, setLastNameKana] = React.useState('');
  /** 代表者氏名（全角カナ）の名。 */
  const [firstNameKana, setFirstNameKana] = React.useState('');
  /** プラカードの項目で設定する名前。（自由入力） */
  const [placard, setPlacard] = React.useState('');
  /** 選択車種。 */
  const [carType, setCarType] = React.useState('');
  /** 大人の人数。 */
  const [adultNum, setAdultNum] = React.useState(1);
  /** 子供の人数。 */
  const [childNum, setChildNum] = React.useState(0);
  /** 台数。 */
  const [carNum, setCarNum] = React.useState(1);
  /** 電話番号。 */
  const [phoneNumber, setPhoneNumber] = React.useState('');
  /** スーツケースの個数。 */
  const [suitcase, setSuitcase] = React.useState(0);
  /** スーツケースの最大個数。 */
  const [maxSuitcase, setmaxSuitcase] = React.useState(4);
  /** 車1台あたりのスーツケースの最大数 */
  const [maxSuitcasePerUnit, setMaxSuitcasePerUnit] = React.useState(4);
  /** ゴルフバッグの個数。 */
  const [golfBag, setGolfBag] = React.useState(0);
  /** ゴルフバッグの個数。 */
  const [maxGolfBag, setMaxGolfBag] = React.useState(3);
  /** 車1台あたりのゴルフバッグの最大数 */
  const [maxGolfBagPerUnit, setMaxGolfBagPerUnit] = React.useState(3);
  /** その他(※車椅子など)荷物の個数。 */
  const [otherBag, setOtherBag] = React.useState(0);
  /** 支払い方法。 */
  const [payment, setPayment] = React.useState('');
  /** 領収書宛名。 */
  const [receiptAddress, setReceiptAddress] = React.useState('');
  /** 備考 */
  const [remarks, setRemarks] = React.useState('');
  /** 料金情報 */
  const [ reservationFare, setReservationFare ] = React.useState<ReservationFare>(defaultReservationFare);
  /** 各種ラベル */
  const [ reservationLabels, setReservationLabels ] = React.useState<ReservationLabels>(defaultReservationLabels);
  /** 誤って先方に予約完了メールが届くのを防止する目的で、仮予約画面でユーザーが入力したメールアドレスを設定する */
  const mailAddressCookieName = 'ana_airport_access_navi_email_address';
  // 本番以外ではCookieに保存されていたメールアドレスを呼び出す
  const defaultOperatorMailaddress: string = (()=>{
    if (Environment.getEnvironmentType() !== Environment.PRODUCTION) {
      return Cookies.get(mailAddressCookieName) || '';
    }
    return '';
  })()
  /** 開発・ステージング環境でのみ設定可能とするタクシー会社のメールアドレスとして扱うメールアドレス。 */
  const [operatorMailaddress, setOperatorMailaddress] = React.useState<string>(defaultOperatorMailaddress);
  /** 開発・ステージング環境でのみ設定可能: 事業者用メール配信失敗再現 */
  const [ mailUndeliveredProblemReproduction, setMailUndeliveredProblemReproduction ] = React.useState<string>('');
  /** ANAマイレージクラブ（ACM番号） */
  const [acmMileageNumber, setAcmMileageNumber] = React.useState('');

  /** 予約処理の実行中であるかを表すフラグ。 */
  const [isSubmitting, switchSubmitting] = React.useState(false);

  /** 大人の人数が変更されたときの処理。 */
  const onChangeAdultNum = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    const value = e.target.value as number;
    setAdultNum(value);
  }

  /** 子供の人数が変更されたときの処理。 */
  const onChangeChildNum = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    const value = e.target.value as number;
    setChildNum(value);
  }

  /** タクシーの台数が変更されたときの処理。 */
  const onChangeCarNum = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    const value = e.target.value as number;
    setCarNum(value);
    // 台数が変わると荷物の最大数が変わるためリセットする（0個にする）。
    setSuitcase(0);
    setGolfBag(0);
  }

  /** スーツケースの個数が変更されたときの処理。 */
  const onChangeSuitcase = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    const value = e.target.value as number;
    setSuitcase(value);
  }

  /** ゴルフバッグの個数が変更されたときの処理。 */
  const onChangedGolfBag = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    const value = e.target.value as number;
    setGolfBag(value);
  }

  /** その他荷物(※車椅子など)の個数が変更されたときの処理。 */
  const onChangeOtherBag = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    const value = e.target.value as number;
    setOtherBag(value);
  }

  // タクシー台数が変更されたときに実行する処理。
  React.useEffect(() => {
    /** スーツケースの個数の最大値。（X個 * タクシー台数。） */
    const maxSuitcase = carNum * maxSuitcasePerUnit;
    /** ゴルフバッグの個数の最大値（X個 * タクシー台数。） */
    const maxGolfBag = carNum * maxGolfBagPerUnit;
    // スーツケースの個数の最大値を更新する。
    setmaxSuitcase(maxSuitcase);
    // ゴルフバッグの個数の最大値を更新する。
    setMaxGolfBag(maxGolfBag);
  }, [carNum]);

  return {
    requestCount, setRequestCount,
    isFailed, setIsFailed,
    isRequiredRetry, setIsRequiredRetry,
    isUnavalableEmail, setIsUnavalableEmail,
    isInvalidInput, setIsInvalidInput,
    lastName, setLastName,
    firstName, setFirstName,
    lastNameKana, setLastNameKana,
    firstNameKana, setFirstNameKana,
    placard, setPlacard,
    carType, setCarType,
    adultNum, setAdultNum,onChangeAdultNum,
    childNum, setChildNum, onChangeChildNum,
    carNum, setCarNum, onChangeCarNum,
    phoneNumber, setPhoneNumber,
    suitcase, setSuitcase, onChangeSuitcase,
    maxSuitcase,
    golfBag, setGolfBag, onChangedGolfBag,
    maxGolfBag,
    otherBag, setOtherBag, onChangeOtherBag,
    payment, setPayment,
    receiptAddress, setReceiptAddress,
    remarks, setRemarks,
    operatorMailaddress, setOperatorMailaddress,
    isSubmitting, switchSubmitting,
    acmMileageNumber, setAcmMileageNumber,
    reservationFare, setReservationFare,
    reservationLabels, setReservationLabels,
    setMaxSuitcasePerUnit, setMaxGolfBagPerUnit,
    setmaxSuitcase, setMaxGolfBag,
    mailUndeliveredProblemReproduction, setMailUndeliveredProblemReproduction
  }
}
